import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
} from "@mui/material";
import clsx from "clsx";
import { JsonObject } from "fe-common/graphql";
import BackupIcon from "@mui/icons-material/Backup";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import { JsonEditButton } from "./jsonEditButton";
import { JsonEditor as Editor } from "jsoneditor-react";
import "jsoneditor-react/es/editor.min.css";
import { JsonEditConformSave } from "./jsonEditConformSave";

//region [[ Styles ]]

const ClinicJsonSettingsEditView = styled(Accordion)({
  margin: "5px 0",
  width: "100%",
});

const Title = styled(AccordionSummary)({
  textTransform: "capitalize",
});

const Content = styled(AccordionDetails)({
  display: "flex",
  flexFlow: "column",
  "& .jsoneditor-outer": {
    height: "500px",
  },
});

const ActionsArea = styled("div")({
  width: "100%",
  display: "flex",
  background: "#3883fa",
  flexFlow: "row-reverse",
  height: "42px",
});

const DoneIcon = styled(CloudDoneIcon)({
  fill: "yellowgreen",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface ClinicJsonSettingsEditProps {
  className?: string;
  keyName: string;
  value: JsonObject;
  onSubmit: (newValue: JsonObject) => void;
  onResetDefaultClicked: () => void;
  disableWarning?: boolean;
  open?: boolean;
  disabled?: boolean;
  isMasterClinic?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const JsonSettingsEdit = ({
  onSubmit,
  onResetDefaultClicked,
  value,
  ...props
}: ClinicJsonSettingsEditProps) => {
  const [currentValue, setValue] = useState(value || {});

  const [saved, setSaved] = useState(false);

  const [dirty, setDirty] = useState(false);

  const [showAreYouSure, setShowAreYouSure] = useState(false);

  const editorRef = useRef(null);

  useEffect(() => {
    try {
      setValue(value || {});
      // console.log("#### NEW VALUE!!!! ####", editorRef);
      if (editorRef.current?.jsonEditor) {
        editorRef.current.jsonEditor.set(value);
      }
    } catch (e) {
      console.log(e);
    }
  }, [value]);

  const handleSaveIsApproved = useCallback(() => {
    // console.log("saving json", currentValue);
    setDirty(false);
    setShowAreYouSure(false);
    setSaved(true);
    onSubmit?.(currentValue);
  }, [setDirty, onSubmit, currentValue]);

  const onSaveClicked = useCallback(() => {
    if (props.disableWarning) {
      return handleSaveIsApproved();
    }
    setShowAreYouSure(true);
  }, [setShowAreYouSure, handleSaveIsApproved]);

  useEffect(() => {
    if (saved && dirty) {
      setSaved(false);
    }
  }, [saved, dirty, setSaved]);

  const handleSaveIsCanceled = useCallback(() => setShowAreYouSure(false), [
    setShowAreYouSure,
  ]);

  const onReset = useCallback(() => {
    setDirty(true);
    if (onResetDefaultClicked) return onResetDefaultClicked();
    setValue("");
  }, [onResetDefaultClicked, setValue, setDirty]);

  const handleJsonChange = useCallback(
    (newValue) => {
      console.log("json changed", newValue);
      setValue(Array.isArray(newValue) ? [...newValue] : { ...newValue });
      setDirty(true);
    },
    [setValue, setDirty],
  );

  return (
    <ClinicJsonSettingsEditView
      className={clsx(props.className, "clinic-json-settings-edit")}
      id={props.keyName}
      expanded={props.open}
    >
      {showAreYouSure && (
        <JsonEditConformSave
          onApprove={handleSaveIsApproved}
          onCancel={handleSaveIsCanceled}
          isMasterClinic={props.isMasterClinic}
        />
      )}
      <Title>{props.keyName.trim()}</Title>
      <Content>
        <ActionsArea>
          <JsonEditButton
            icon={saved ? <DoneIcon /> : <BackupIcon />}
            label={saved ? "Saved" : "Save"}
            onClick={onSaveClicked}
            disabled={!dirty || props.disabled}
          />
          <JsonEditButton
            icon={<CloudQueueIcon />}
            label={"Reset defaults"}
            onClick={onReset}
            disabled={props.disabled}
          />
        </ActionsArea>
        <Editor
          value={currentValue}
          onChange={handleJsonChange}
          mode={"view"}
          allowedModes={["code", "tree", "view"]}
          ref={editorRef}
        />
      </Content>
    </ClinicJsonSettingsEditView>
  );
};
